/**
 * ImpersonateBanner Component
 *
 * Banner fixo no topo da tela quando o usuário está em modo impersonate.
 * Mostra informações do usuário original e do usuário sendo acessado.
 *
 * @since 2025-01-10
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useImpersonate } from '@/hooks/useImpersonate';

export function ImpersonateBanner() {
  const { t } = useTranslation();
  const {
    isImpersonating,
    originalUser,
    impersonatedUser,
    exitImpersonate,
    isExiting,
  } = useImpersonate();

  // Não renderizar se não está impersonando
  if (!isImpersonating || !impersonatedUser) {
    return null;
  }

  const handleExit = async () => {
    try {
      await exitImpersonate();
    } catch (error) {
      console.error('Erro ao sair do impersonate:', error);
    }
  };

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-white shadow-md"
      style={{ height: '48px' }}
    >
      <div className="h-full max-w-full mx-auto px-4 flex items-center justify-between">
        {/* Info do impersonate */}
        <div className="flex items-center gap-3">
          {/* Icon */}
          <div className="flex items-center justify-center w-8 h-8 bg-amber-600 rounded-full">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </div>

          {/* Texto */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
            <span className="text-sm font-medium">
              {t('impersonate.bannerMessage', 'Você está acessando como')}
            </span>
            <span className="text-sm font-bold">
              {impersonatedUser.name}
            </span>
            <span className="hidden sm:inline text-xs text-amber-100">
              {t('impersonate.profileLabel', 'Perfil')}:{' '}
              {impersonatedUser.primaryRole && impersonatedUser.primaryRole.length > 0
                ? impersonatedUser.primaryRole
                : '—'}
            </span>
            {originalUser && (
              <span className="hidden sm:inline text-xs text-amber-100">
                ({t('impersonate.originalUser', 'Usuário original')}: {originalUser.name})
              </span>
            )}
          </div>
        </div>

        {/* Botão de voltar */}
        <button
          onClick={handleExit}
          disabled={isExiting}
          className="
            flex items-center gap-2 px-4 py-1.5
            bg-amber-600 hover:bg-amber-700
            disabled:bg-amber-400 disabled:cursor-not-allowed
            rounded-lg text-sm font-medium
            transition-colors duration-200
          "
        >
          {isExiting ? (
            <>
              <svg
                className="animate-spin h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span>{t('impersonate.exiting', 'Saindo...')}</span>
            </>
          ) : (
            <>
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z"
                />
              </svg>
              <span>{t('impersonate.exitButton', 'Voltar')}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default ImpersonateBanner;
